import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ContainerVisitService } from '@common/services/container-visit.service';
import { ServiceRequestService } from '@common/services/service-request.service';
import { ShipmentPoolService } from '@common/services/shipment-pool.service';
import { marker } from '@common/translate-marker';
import { requiredValidator } from '@common/validators/required-validator';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ToastService } from '@services/toast.service';
import { ServiceRequestItemsHoldModalModel } from 'app/tos-operations/models/ServiceRequestItemsHoldModal.model';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-service-request-items-hold-modal',
    templateUrl: './service-request-items-hold-modal.component.html',
    styleUrls: ['./service-request-items-hold-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ServiceRequestItemsHoldModalComponent implements OnInit, OnDestroy {
    public title: string;
    public message: string;
    public serviceRequestVersionId: number;
    public serviceRequestIds: any[];
    public serviceRequestItemIds: any[];
    public shipmentItemIds: any[];
    public containerItemIds: any[];
    public containerVisitIds: any[];
    public shipmentPoolIds: any[];
    public shipmentPoolItemIds: any[];
    public cargoHoldTypeId: any;
    public cargoHoldRemark: string;
    public isUnholdAction: boolean;
    public shipmentType: string;
    public activeHoldTypeIds: any[];
    public isUnholdRoleIdAvailable: boolean;

    model: ServiceRequestItemsHoldModalModel = new ServiceRequestItemsHoldModalModel();
    form: UntypedFormGroup;
    isBusy: boolean;
    cargoHoldTypes: any[] = [];
    commonActiveHoldTypeIds: any[] = [];
    destroyed$ = new Subject<void>();

    public attachmentFileUploadRestrictions: FileRestrictions = {
        maxFileSize: 5_242_880,
        allowedExtensions: ['.pdf']
    };
    files: Array<any> = [];

    constructor(
        public activeModal: DialogRef,
        private serviceRequestService: ServiceRequestService,
        private containerVistiService: ContainerVisitService,
        private shipmentPoolService: ShipmentPoolService,
        private translateService: TranslateService,
        private toastyService: ToastService
    ) {}

    ngOnInit(): void {
        this.isBusy = true;
        this.form = this.model.createFormGroup();
        if (this.isUnholdAction) {
            this.getCommonHoldTypes();
        } else {
            this.getCargoHoldTypes();
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    holdServiceRequestItems() {
        this.isBusy = true;
        const model = this.isUnholdAction
            ? { ...this.form.getRawValue(), operation: 'Unhold' }
            : { ...this.form.getRawValue(), operation: 'Hold' };
        model.serviceRequestVersionId = this.serviceRequestVersionId;
        model.serviceRequestIds = this.serviceRequestIds?.map((x) => x.id);
        model.serviceRequestItemIds = this.serviceRequestItemIds?.map((x) => x.id);
        model.shipmentItemIds = this.shipmentItemIds?.map((x) => x.id);
        model.containerItemIds = this.containerItemIds?.map((x) => x.id);
        model.containerVisitIds = this.containerVisitIds?.map((x) => x.id);
        model.shipmentTypeId = this.shipmentType;
        model.shipmentPoolIds = this.shipmentPoolIds?.map((x) => x.id);
        model.shipmentPoolItemIds = this.shipmentPoolItemIds?.map((x) => x.id);
        model.files = this.files;

        let serviceRequestObservable;
        if (this.serviceRequestVersionId && !model.serviceRequestItemIds?.length) {
            serviceRequestObservable = this.serviceRequestService.holdServiceRequest(model);
        } else if (this.serviceRequestIds?.length) {
            serviceRequestObservable = this.serviceRequestService.holdServiceRequests(model);
        } else if (this.containerVisitIds) {
            serviceRequestObservable = this.containerVistiService.holdContainerVisits(model);
        } else if (this.shipmentPoolIds) {
            serviceRequestObservable = this.shipmentPoolService.holdShipmentPools(model);
        } else if (this.shipmentPoolItemIds) {
            serviceRequestObservable = this.shipmentPoolService.holdShipmentPoolItems(model);
        } else {
            serviceRequestObservable = this.serviceRequestService.holdServiceRequestItems(model);
        }

        serviceRequestObservable.subscribe({
            next: () => {
                this.isBusy = false;
                this.activeModal.close(true);
            },
            error: (error) => {
                this.isBusy = false;
                this.activeModal.close();
            }
        });
    }

    getCommonHoldTypes() {
        if (this.serviceRequestVersionId && !this.serviceRequestItemIds?.length) {
            this.commonActiveHoldTypeIds = this.activeHoldTypeIds;

            if (this.commonActiveHoldTypeIds?.length === 1) {
                this.form.get('cargoHoldTypeId').patchValue(this.commonActiveHoldTypeIds[0]);
            } else if (this.commonActiveHoldTypeIds?.length > 1 && this.commonActiveHoldTypeIds.includes(undefined)) {
                this.showActionNotPossibleError();
            } else if (this.commonActiveHoldTypeIds?.length > 1) {
                this.getCargoHoldTypes(true);
            } else {
                this.showActionNotPossibleError();
            }
        } else {
            const finalArray =
                this.shipmentItemIds?.length > 0
                    ? this.shipmentItemIds
                    : this.containerItemIds?.length > 0
                      ? this.containerItemIds
                      : this.serviceRequestItemIds?.length > 0
                        ? this.serviceRequestItemIds
                        : this.containerVisitIds?.length > 0
                          ? this.containerVisitIds
                          : this.serviceRequestIds?.length > 0
                            ? this.serviceRequestIds
                            : this.shipmentPoolIds?.length > 0
                              ? this.shipmentPoolIds
                              : this.shipmentPoolItemIds;
            if (finalArray?.length > 0) {
                if (this.isUnholdRoleIdAvailable) {
                    const activeHoldTypeArrays = this.activeHoldTypeIds;
                    this.commonActiveHoldTypeIds = activeHoldTypeArrays?.reduce((commonIds, activeHoldTypeIds) => {
                        return commonIds?.filter((id) => activeHoldTypeIds?.includes(id));
                    });
                } else {
                    const activeHoldTypeArrays = finalArray?.map((item) => item?.activeHoldTypeIds);
                    this.commonActiveHoldTypeIds = activeHoldTypeArrays?.reduce((commonIds, activeHoldTypeIds) => {
                        return commonIds?.filter((id) => activeHoldTypeIds?.includes(id));
                    });
                }

                if (this.commonActiveHoldTypeIds?.length === 1) {
                    this.form.get('cargoHoldTypeId').patchValue(this.commonActiveHoldTypeIds[0]);
                }
                // if common active hold type ids conatins undefind,
                else if (this.commonActiveHoldTypeIds?.length > 1 && this.commonActiveHoldTypeIds.includes(undefined)) {
                    this.showActionNotPossibleError(finalArray);
                } else if (this.commonActiveHoldTypeIds?.length > 1) {
                    this.getCargoHoldTypes(true);
                } else {
                    this.showActionNotPossibleError(finalArray);
                }
            }
        }
        this.isBusy = false;
    }

    private showActionNotPossibleError(finalArray?: any[]) {
        // No "current selection" wording for single-entity contexts (detail pages):
        // either an entire SR/CV/etc. is being held, or only one item is in finalArray.
        const isSingleEntityContext = !finalArray || finalArray.length === 1;
        const messageKey = isSingleEntityContext
            ? marker('Action is not possible.')
            : marker('Action is not possible on current selection.');
        this.toastyService.error(this.translateService.instant(messageKey));
        this.activeModal.close();
    }

    fileSelected($event) {
        if ($event.files.length > 10) {
            this.toastyService.error(this.translateService.instant(marker('Please select maximum of 10 files')));
            $event.preventDefault();
            return;
        }

        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Attachment size invalid'))} - ${element.name}`
                );
                return;
            }
            if (!this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Invalid attachment'))} - ${element.name}`
                );
                return;
            }
            if (element.extension != '.pdf') {
                this.toastyService.error('Please select only PDF files');
                $event.preventDefault();
                return;
            }
            const raw = element.rawFile;
            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    fileRemoved($event) {
        $event.files.forEach((element) => {
            const file = this.files.filter((x) => x.base64ContentName == element.name);
            if (file == null || file.length == 0) return;
            this.files.splice(this.files.indexOf(file[0]), 1);
        });
    }

    documentOnLoad(file: string, name: string) {
        this.files.push({ base64Content: file, base64ContentName: name });
    }

    private getCargoHoldTypes(isForUnhold: boolean = false) {
        this.serviceRequestService.getCargoHoldTypesForUser().subscribe((data) => {
            this.cargoHoldTypes = isForUnhold ? data.filter((x) => this.commonActiveHoldTypeIds.includes(x.id)) : data;
            this.form.get('cargoHoldTypeId').setValidators(requiredValidator());
            this.isBusy = false;
        });
    }
}
